import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { CardView } from "@/components/v2/CardView";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useAccessParents } from "@/pages/infrastructure/functions";

import { useContext, useMemo } from "react";

import type { TerminalCategoryOptionsEnum } from "server/dwhApi/terminals/types.js";

import { HomePageContext } from "../../context";
import { StreamGroup } from "./components/streamGroup";
import {
  getRecommendedWasteStreamsFromPortfolio,
  getWasteStreamsAtBuilding,
} from "./utils";

export const WasteStreamsInUse: React.FC = () => {
  const { timeFilter, selectedTenantCategoryIds } = useContext(HomePageContext);
  const { currentTerminal } = useTerminalsState();
  const { wasteTypes: sanityWasteTypes } = useCommonEntitiesStore();
  const { config } = useConfig();

  const {
    manageAccessParentPageData: wasteRoomData,
    isLoading: isLoadingAccessParents,
  } = useAccessParents();

  const { data, isLoading: isLoadingTerminal } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminal",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!timeFilter && !!currentTerminal,
      },
    );

  const { data: aggregatedData, isLoading: isLoadingAggregates } =
    trpc.dwh.terminals.getAggregates.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        commercialTerminalCategory:
          currentTerminal?.commercialRealEstateCategory as TerminalCategoryOptionsEnum,
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalAggregates",
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            currentTerminal?.commercialRealEstateCategory,
          ].toString(),
        enabled: !!timeFilter && !!currentTerminal,
      },
    );

  const showLoading = useMemo(
    () => isLoadingAccessParents || isLoadingTerminal || isLoadingAggregates,
    [isLoadingAccessParents, isLoadingAggregates, isLoadingTerminal],
  );

  const wasteTypeClassificationSystem = useMemo(
    () =>
      currentTerminal?.fractionDiscriminator ||
      config.wasteTypeClassificationSystemId ||
      "",
    [currentTerminal, config],
  );

  const wasteTypesAtBuilding = useMemo(
    () => getWasteStreamsAtBuilding({ sanityWasteTypes, data, wasteRoomData }),
    [sanityWasteTypes, data, wasteRoomData],
  );

  const wasteTypesRecommended = useMemo(
    () =>
      getRecommendedWasteStreamsFromPortfolio({
        data,
        sanityWasteTypes,
        wasteRoomData,
        aggregatedData,
        wasteTypeClassificationSystem,
        wasteTypesAtBuilding,
      }),
    [
      aggregatedData,
      data,
      wasteRoomData,
      sanityWasteTypes,
      wasteTypeClassificationSystem,
      wasteTypesAtBuilding,
    ],
  );

  return (
    <CardView
      title="homePage:wasteStreamsInUseTitle"
      subtitle="homePage:wasteStreamsInUseSubtitle"
    >
      <ViewWithLoader isLoading={showLoading}>
        <div className="mt-3 flex flex-col gap-[22px]">
          <StreamGroup
            label="homePage:wasteStreamsInUseAtBuilding"
            streams={wasteTypesAtBuilding}
          />
          <StreamGroup
            label="homePage:wasteStreamsInUseRecommended"
            streams={wasteTypesRecommended}
          />
        </div>
      </ViewWithLoader>
    </CardView>
  );
};
